import * as React from 'react'

import { GlobeIcon, ImageIcon } from 'lucide-react'

import {
  GROUP_INDENT_STEP_PX,
  GroupConnectorLines,
  GroupHeader,
  GroupHeaderTrigger,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'

import {
  LBS_CONNECTOR_OFFSET_PX,
  MapViewCapability,
} from './MapViewsSidebarListItem'

// Depth grid shared with `GroupIndent` / `GroupConnectorLines` (chevron + gap per level).
export const LBS_LEVEL_INDENT_PX = GROUP_INDENT_STEP_PX

interface IBaseHeaderRow {
  key: string
  bucketKey: string
  label: string
  count: number
}

export interface ITypeHeaderRow extends IBaseHeaderRow {
  headerKind: 'type'
  capability: MapViewCapability
}

export interface IScopeHeaderRow extends IBaseHeaderRow {
  headerKind: 'scope'
  scopeIconName: MapViewLocationIcon
  scopeColor?: string
}

export interface ILbsHeaderRow extends IBaseHeaderRow {
  headerKind: 'lbs'
  /** Nesting depth — 0 root, 1+ for nested LBS attributes. */
  level: number
  scopeIconName: MapViewLocationIcon
  scopeColor?: string
}

export type IGroupHeaderRow = ITypeHeaderRow | IScopeHeaderRow | ILbsHeaderRow

interface IMapViewsGroupHeaderProps {
  header: IGroupHeaderRow
  collapsed: boolean
  onToggle: () => void
  lbsLineLevel?: number
}

interface IBandSlots {
  labelIcon: React.ReactNode
}

const resolveBandSlots = (header: IGroupHeaderRow): IBandSlots => {
  switch (header.headerKind) {
    case 'lbs':
    case 'scope':
      return {
        labelIcon: (
          <LocationIcon
            iconName={header.scopeIconName}
            color={header.scopeColor}
            className="tw:size-4 tw:shrink-0"
          />
        ),
      }
    case 'type':
      return {
        labelIcon:
          header.capability === 'georeferenced' ? (
            <GlobeIcon
              className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
              aria-hidden="true"
            />
          ) : (
            <ImageIcon
              className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
              aria-hidden="true"
            />
          ),
      }
  }
}

export const MapViewsGroupHeader: React.FC<IMapViewsGroupHeaderProps> = ({
  header,
  collapsed,
  onToggle,
  lbsLineLevel,
}) => {
  const { labelIcon } = resolveBandSlots(header)
  return (
    <GroupHeader
      role="group"
      aria-label={header.label}
      data-slot="map-views-group-header"
      className="tw:sticky tw:top-0 tw:z-10"
    >
      <GroupConnectorLines
        depth={lbsLineLevel ?? 0}
        leftOffsetPx={LBS_CONNECTOR_OFFSET_PX}
      />
      <GroupHeaderTrigger
        expanded={!collapsed}
        onClick={onToggle}
        ariaLabel={header.label}
        leading={
          <GroupIndent depth={lbsLineLevel ?? 0} stepPx={LBS_LEVEL_INDENT_PX} />
        }
        labelIcon={labelIcon}
        label={header.label}
        count={header.count}
      />
    </GroupHeader>
  )
}
